/* ==========================================================================
   YUMMY GUMMY — scroll & motion controller (GSAP + ScrollTrigger)
   --------------------------------------------------------------------------
   Robustness contract:
     - If GSAP is missing (CDN blocked) -> reveal everything, never hide.
     - If reduced-motion or animations disabled -> the <html> never gets the
       .yg-anim class, so CSS keeps content visible and this script bails.
     - Mobile gets lighter effects (no parallax / pinning).
     - Re-scans on shopify:section:load so the Theme Editor stays correct.
     - Everything runs in try/catch; a load watchdog reveals content if a
       script error ever leaves elements hidden.

   Markup hooks (data attributes):
     [data-yg-reveal]            fade/slide in on scroll (up|down|left|right|scale)
     [data-yg-stagger]           stagger direct children in on scroll
     [data-yg-float="14"]        gentle continuous float (amplitude in px)
     [data-yg-parallax="-0.15"]  scroll parallax, desktop only (speed factor)
     [data-yg-hero]              hero intro timeline container
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var cfg = (window.YGConfig = window.YGConfig || {});
  var DESKTOP_QUERY = '(min-width: 990px)';

  // --- Watchdog: if anything goes wrong, never leave content hidden --------
  function revealAll() {
    root.classList.remove('yg-anim');
  }
  window.addEventListener('load', function () {
    window.setTimeout(function () {
      if (!window.__ygAnimDone && root.classList.contains('yg-anim')) {
        revealAll();
      }
    }, 1500);
  });

  // Animations were never switched on (disabled, or reduced-motion). The CSS
  // leaves everything visible in that case, so there is nothing to do.
  if (!root.classList.contains('yg-anim')) return;

  // GSAP failed to load -> reveal content and stop.
  if (!window.gsap) {
    revealAll();
    return;
  }

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger || null;
  if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }
  function liteMobile() {
    return cfg.liteMobile !== false; // default: lighter on mobile
  }
  function each(scope, selector, fn) {
    var nodes = (scope || document).querySelectorAll(selector);
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }

  /* --- Reveal on scroll --------------------------------------------------- */
  function setupReveals(scope) {
    var els = [];
    each(scope, '[data-yg-reveal]', function (el) {
      if (!el.__ygBound) {
        el.__ygBound = true;
        els.push(el);
      }
    });
    if (!els.length) return;

    if (!ScrollTrigger) {
      gsap.to(els, { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.6, stagger: 0.06 });
      return;
    }
    ScrollTrigger.batch(els, {
      start: 'top 86%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.09,
          onComplete: function () {
            batch.forEach(function (el) {
              el.classList.add('yg-revealed');
            });
          }
        });
      }
    });
  }

  /* --- Staggered groups --------------------------------------------------- */
  function setupStaggers(scope) {
    each(scope, '[data-yg-stagger]', function (group) {
      if (group.__ygBound) return;
      group.__ygBound = true;
      var kids = group.children;
      if (!kids.length) return;
      var play = function () {
        gsap.to(kids, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08 });
      };
      if (ScrollTrigger) {
        ScrollTrigger.create({ trigger: group, start: 'top 85%', once: true, onEnter: play });
      } else {
        play();
      }
    });
  }

  /* --- Continuous float --------------------------------------------------- */
  function setupFloats(scope) {
    if (!isDesktop() && liteMobile()) return; // skip floats on small screens
    each(scope, '[data-yg-float]', function (el, i) {
      if (el.__ygFloat) return;
      el.__ygFloat = true;
      var amp = parseFloat(el.getAttribute('data-yg-float')) || 14;
      gsap.to(el, {
        y: '+=' + amp,
        rotation: '+=' + (i % 2 ? 1.5 : -1.5),
        duration: 2.4 + (i % 3) * 0.35,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    });
  }

  /* --- Parallax (desktop only) ------------------------------------------- */
  function setupParallax(scope) {
    if (!ScrollTrigger || !isDesktop()) return;
    each(scope, '[data-yg-parallax]', function (el) {
      if (el.__ygParallax) return;
      el.__ygParallax = true;
      var speed = parseFloat(el.getAttribute('data-yg-parallax')) || -0.15;
      gsap.to(el, {
        yPercent: speed * 100,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* --- Hero intro timeline ------------------------------------------------ */
  function setupHero(scope) {
    each(scope, '[data-yg-hero]', function (hero) {
      if (hero.__ygHero) return;
      hero.__ygHero = true;

      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      var q = function (sel) {
        return hero.querySelectorAll(sel);
      };
      if (q('[data-yg-hero-eyebrow]').length)
        tl.to(q('[data-yg-hero-eyebrow]'), { opacity: 1, y: 0, duration: 0.5 });
      if (q('.yg-word').length)
        tl.to(q('.yg-word'), { opacity: 1, y: 0, duration: 0.8, stagger: 0.05 }, '-=0.1');
      if (q('[data-yg-hero-anim]').length)
        tl.to(q('[data-yg-hero-anim]'), { opacity: 1, y: 0, duration: 0.6, stagger: 0.08 }, '-=0.5');
      if (q('.yg-hero__ring').length)
        tl.to(q('.yg-hero__ring'), { opacity: 0.9, scale: 1, duration: 0.9 }, '-=0.7');
      if (q('.yg-hero__product').length)
        tl.to(q('.yg-hero__product'), { opacity: 1, y: 0, duration: 0.9 }, '-=0.7');
      if (q('.yg-hero__float-tag').length)
        tl.to(q('.yg-hero__float-tag'), { opacity: 1, scale: 1, duration: 0.5, stagger: 0.15 }, '-=0.4');

      // gentle parallax drift of the product as the hero scrolls away (desktop)
      if (ScrollTrigger && isDesktop()) {
        var product = hero.querySelector('.yg-hero__product');
        if (product) {
          gsap.to(product, {
            yPercent: -12,
            ease: 'none',
            scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
          });
        }
      }
    });
  }

  /* --- Orchestration ------------------------------------------------------ */
  function setupScope(scope) {
    [setupHero, setupReveals, setupStaggers, setupFloats, setupParallax].forEach(function (fn) {
      try {
        fn(scope || document);
      } catch (e) {
        if (window.console) console.warn('[YG] animation step failed:', e);
      }
    });
  }

  function init() {
    setupScope(document);
    window.__ygAnimDone = true;
    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  // Public hook for other scripts (e.g. cart updates) to recalc positions.
  window.YGAnim = {
    refresh: function () {
      if (ScrollTrigger) ScrollTrigger.refresh();
    },
    rescan: setupScope
  };

  // --- Theme Editor support ----------------------------------------------
  document.addEventListener('shopify:section:load', function (e) {
    setupScope(e.target);
    if (ScrollTrigger) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!ScrollTrigger) return;
    ScrollTrigger.getAll().forEach(function (st) {
      if (st.trigger && e.target.contains(st.trigger)) st.kill();
    });
    ScrollTrigger.refresh();
  });
  ['shopify:section:select', 'shopify:section:reorder'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      if (ScrollTrigger) ScrollTrigger.refresh();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
